import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { formatEther, parseEther } from 'viem';
import { CONTRACTS, ABIS } from '../contracts/config';
import nctTokenABI from '../contracts/NCTToken.json';
import { message } from 'antd';
import { useEffect } from 'react';

// 轮询间隔配置
const FAST_POLLING_INTERVAL = 5000; // 5秒快速轮询

// 使用NCTToken.json中的abi属性
const NCT_TOKEN_ABI = nctTokenABI.abi;

interface UseNctBalanceReturn {
  // 余额相关
  balance: string;
  balanceWei: bigint | undefined;
  isLoading: boolean;
  refetch: () => void;
  error: Error | null;
  
  // 授权相关
  getAllowance: (spender: string) => string;
  checkAllowance: (spender: string, amount: string) => boolean;
  approve: (spender: string, amount?: string) => Promise<boolean>;
  needsApproval: (spender: string, amount: string) => boolean;
  
  // 授权状态
  isApproving: boolean;
  approveHash: string | undefined;
  isApproveConfirming: boolean;
  
  // 刷新函数
  refetchAllowance: (spender: string) => void;
  reset: () => void;
}

/**
 * 获取用户NCT代币余额和授权操作的自定义hook
 * @returns {UseNctBalanceReturn} 包含余额、授权信息和相关操作
 */
export const useNctBalance = (): UseNctBalanceReturn => {
  const { address, isConnected } = useAccount();

  // 读取用户NCT代币余额
  const {
    data: nctBalanceData,
    isLoading: isNctBalanceLoading,
    refetch: refetchNctBalance,
    error: contractError,
  } = useReadContract({
    address: CONTRACTS.NCT_TOKEN,
    abi: NCT_TOKEN_ABI,
    functionName: "balanceOf",
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && !!CONTRACTS.NCT_TOKEN,
      refetchInterval: FAST_POLLING_INTERVAL,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
      staleTime: 0,
      retry: 3,
      retryDelay: 1000,
    },
  });

  // 读取对质押合约的授权额度
  const {
    data: stakingAllowanceData,
    refetch: refetchStakingAllowance,
  } = useReadContract({
    address: CONTRACTS.NCT_TOKEN,
    abi: NCT_TOKEN_ABI,
    functionName: "allowance",
    args: address && CONTRACTS.STAKING ? [address, CONTRACTS.STAKING] : undefined,
    query: {
      enabled: !!address && !!CONTRACTS.STAKING,
      refetchInterval: FAST_POLLING_INTERVAL,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
      staleTime: 0,
    },
  });

  // 读取对购买管理合约的授权额度
  const {
    data: purchaseAllowanceData,
    refetch: refetchPurchaseAllowance,
  } = useReadContract({
    address: CONTRACTS.NCT_TOKEN,
    abi: NCT_TOKEN_ABI,
    functionName: "allowance",
    args: address && CONTRACTS.PurchaseManager ? [address, CONTRACTS.PurchaseManager] : undefined,
    query: {
      enabled: !!address && !!CONTRACTS.PurchaseManager,
      refetchInterval: FAST_POLLING_INTERVAL,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
      staleTime: 0,
    },
  });

  // 授权合约调用
  const {
    writeContract: writeApprove,
    data: approveHash,
    isLoading: isApprovePending,
    reset: resetApprove,
  } = useWriteContract();

  // 等待授权交易确认
  const { isLoading: isApproveConfirming } = useWaitForTransactionReceipt({
    hash: approveHash,
    query: {
      enabled: !!approveHash,
      refetchInterval: 500,
    },
  });

  // 获取指定合约的授权额度
  const getAllowance = (spender: string): string => {
    if (spender === CONTRACTS.STAKING) {
      return stakingAllowanceData ? formatEther(stakingAllowanceData as bigint) : "0";
    } else if (spender === CONTRACTS.PurchaseManager) {
      return purchaseAllowanceData ? formatEther(purchaseAllowanceData as bigint) : "0";
    }
    return "0";
  };

  // 检查是否有足够的授权额度
  const checkAllowance = (spender: string, amount: string): boolean => {
    const allowanceValue = parseFloat(getAllowance(spender));
    const amountValue = parseFloat(amount);
    return allowanceValue >= amountValue;
  };

  // 检查是否需要授权
  const needsApproval = (spender: string, amount: string): boolean => {
    return !checkAllowance(spender, amount);
  };

  // 授权NCT代币给指定合约
  const approve = async (spender: string, amount?: string): Promise<boolean> => {
    if (!isConnected || !address) {
      message.error('请先连接钱包');
      return false;
    }

    if (!spender) {
      message.error('授权地址不能为空');
      return false;
    }

    try {
      // 如果没有指定金额，默认授权最大值
      const approveAmount = amount ? parseEther(amount) : parseEther("115792089237316195423570985008687907853269984665640564039457584007913129639935");
      
      message.info('正在授权...');
      
      writeApprove({
        address: CONTRACTS.NCT_TOKEN,
        abi: NCT_TOKEN_ABI,
        functionName: "approve",
        args: [spender as `0x${string}`, approveAmount],
      });
      
      return true;
    } catch (error) {
      console.error('授权失败:', error);
      const errorMessage = error instanceof Error ? error.message : '授权失败，请重试';
      message.error(errorMessage);
      return false;
    }
  };

  // 刷新指定合约的授权额度
  const refetchAllowance = (spender: string) => {
    if (spender === CONTRACTS.STAKING) {
      refetchStakingAllowance();
    } else if (spender === CONTRACTS.PurchaseManager) {
      refetchPurchaseAllowance();
    }
  };

  // 重置授权状态
  const reset = () => {
    resetApprove();
  };

  // 当钱包断开时重置状态
  useEffect(() => {
    if (!address || !isConnected) {
      reset();
    }
  }, [address, isConnected]);

  return {
    // 余额相关
    balance: nctBalanceData ? formatEther(nctBalanceData as bigint) : "0",
    balanceWei: nctBalanceData as bigint | undefined,
    isLoading: isNctBalanceLoading,
    refetch: refetchNctBalance,
    error: contractError,
    
    // 授权相关
    getAllowance,
    checkAllowance,
    approve,
    needsApproval,
    
    // 授权状态
    isApproving: isApprovePending || isApproveConfirming,
    approveHash,
    isApproveConfirming,
    
    // 刷新函数
    refetchAllowance,
    reset,
  };
}; 